import debugFactory from "debug";
import orderBy from "lodash/orderBy";
import moment from "moment-timezone";

import {
	CommitmentFilterType,
	OutcomeDisplayLocation,
	OutcomeType,
	TeamMemberStatusType,
	TeamType
} from "@teaming/common-enums";

import { upcat } from "@teaming/common-util";

import { alertFactory } from "@teaming/common-ui";

import {
	configureChannels,
	configureSockets
} from "./listeners";

import {
	NEW_COMMITMENT_ID,
	pristineNewCommitmentState,
	pristineState
} from "./state";

import {
	SHORT_DATE_FORMAT,
	commitmentFilterOrder,
	filterSorts
} from "./helpers";

export default function commitmentsStoreFactory( {
	storeName,
	foundation,
	appChannels,
} ) {
	const debug = debugFactory( `teaming:common-team:stores:${ storeName }` );

	const { store, i18n, ioSocket, ioTeam, ioOutcome, } = foundation();
	const { t, } = i18n.global;
	const { ioChannel, } = appChannels;

	const { success, error, info, } = alertFactory( "common-team.stores.Commitments", t );
	// eslint-disable-next-line no-unused-vars
	const alerts = { success, error, info, };

	const getUserProfile = () => store.getters[ "host-userProfile/userProfile" ];

	configureSockets( {
		storeName,
		store,
		ioSocket,
		ioTeam,
		ioOutcome,
	} );

	configureChannels( {
		storeName,
		ioChannel,
		store,
	} );

	const _receiveCommitments = ( state, { commitments, filterType, } ) => {
		const stateCommitments = state.commitmentPages[ filterType ].commitments;

		const updatedCommitments = upcat(
			stateCommitments,
			commitments,
			( c1, c2 ) => c1.id === c2.id
		);

		const updatedPage = {
			commitments: updatedCommitments,
			offset: updatedCommitments.length,
			hasMorePages: ( commitments.length === state.pageSize ),
			hasPaged: true,
		};

		state.commitmentPages = {
			...state.commitmentPages,
			[ filterType ]: updatedPage,
		};

		state.selectedFilterType = filterType;
	};

	const _receiveMetadata = ( state, metadata ) => {
		state.metadata = {
			TOTAL: metadata.totalCount,
			[ CommitmentFilterType.COMPLETE ]: metadata.completed || 0,
			[ CommitmentFilterType.DUE_IN_NEXT_WEEK ]: metadata.dueInNextWeek || 0,
			[ CommitmentFilterType.NO_DUE_DATE ]: metadata.noDueDate || 0,
			[ CommitmentFilterType.OUTSTANDING ]: metadata.outstanding || 0,
			[ CommitmentFilterType.OVERDUE ]: metadata.overdue || 0,
		};
	};

	const teamSpaceMap = {
		[ TeamType.TEAM ]: OutcomeDisplayLocation.TEAM_SPACE,
		[ TeamType.ONE_ON_ONE ]: OutcomeDisplayLocation.ONE_ON_ONE_SPACE,
	};

	return {
		namespaced: true,
		state: pristineState( i18n ),
		getters: {
			isSetup: state => state.isSetup,
			commitmentUpdatesReceived: state => state.commitmentUpdatesReceived,
			commitments( state ) {
				const { selectedFilterType, } = state;
				const { commitments, } = state.commitmentPages[ selectedFilterType ];

				const sort = filterSorts[ selectedFilterType ];
				const sortedCommitments = sort( commitments );

				return [
					state.newCommitment, // new commitment is always first
					...sortedCommitments,
				].filter( c => !!c )
					.map( commitment => {
						let formattedDueDate = "";
						if ( commitment.dueDate ) {
							formattedDueDate = commitment.dueDate.format( SHORT_DATE_FORMAT );
						}

						let formattedCompletedDate = "";
						if ( commitment.completed ) {
							formattedCompletedDate = commitment.completed.format( SHORT_DATE_FORMAT );
						}

						const assignees = commitment.assignments.map( assignment => assignment.assigned );
						const isNewCommitment = ( commitment.id === NEW_COMMITMENT_ID );
						const isBeingEdited = state.commitmentIdBeingEdited === commitment.id;
						const contentBeingEdited = isBeingEdited ?
							state.contentBeingEdited :
							"";
						const isDirty = isBeingEdited ?
							( commitment.content !== state.contentBeingEdited ) :
							false;
						const isContentValid = isBeingEdited ?
							!!state.contentBeingEdited :
							true;
						const isSaving = state.savesInProgress.has( commitment.id );

						return {
							...commitment,
							assignees,
							formattedCompletedDate,
							formattedDueDate,
							isBeingEdited,
							contentBeingEdited,
							isContentValid,
							isDirty,
							isNewCommitment,
							isSaving,
						};
					} );
			},
			commitmentBeingEdited( state, getters ) {
				return getters.commitments.find( commitment => commitment.isBeingEdited );
			},
			filteredCommitmentCounts( state ) {
				const filters = Object.keys( CommitmentFilterType ).map( type => {
					let count = state.metadata[ type ];
					if ( type === CommitmentFilterType.OUTSTANDING && !!state.newCommitment ) {
						count += 1;
					}
					return {
						type,
						count,
					};
				} );
				return orderBy( filters, filter => commitmentFilterOrder[ filter.type ] );
			},
			hasMorePages( state ) {
				const { selectedFilterType, } = state;
				return state.commitmentPages[ selectedFilterType ].hasMorePages;
			},
			isCreatingNewCommitment: state => !!state.newCommitment,
			pageSize( state ) {
				return state.pageSize;
			},
			pagingOffset( state ) {
				const { selectedFilterType, } = state;
				return state.commitmentPages[ selectedFilterType ].offset;
			},
			permitPaging( state ) {
				return state.permitPaging;
			},
			selectedFilterType( state ) {
				return state.selectedFilterType;
			},
			/** used in listeners */
			teamId: state => state.team.id,
			teamName: state => state.team.name,
			teamType: state => state.team.type,
			teamMembers: state => state.team.members.filter( m => m.status === TeamMemberStatusType.ACTIVE ),
			teamSpace: state => teamSpaceMap[ state.team.type ] || OutcomeDisplayLocation.MY_ACTION_ITEMS,
		},
		mutations: {
			clearNewCommitment( state ) {
				state.newCommitment = null;
				state.commitmentIdBeingEdited = "";
				state.contentBeingEdited = "";
			},
			createNewCommitment( state ) {
				const userProfile = getUserProfile();
				const { personId, } = userProfile;
				const { id: teamId, name: teamName, type: teamType, } = state.team;

				let newCommitment;
				if ( teamId ) {
					// team commitment
					newCommitment = pristineNewCommitmentState( { personId, teamId, teamName, teamType, } );
				} else {
					// personal commitment
					newCommitment = pristineNewCommitmentState( { personId, type: OutcomeType.PERSONAL_ACTION_ITEM, } );
					newCommitment.assignees = [
						userProfile,
					];
					newCommitment.assignments = [ {
						assigned: userProfile,
					}, ];
				}

				state.newCommitment = newCommitment;
				state.commitmentIdBeingEdited = NEW_COMMITMENT_ID;
				state.contentBeingEdited = newCommitment.content;
			},
			markCommitmentForTextEdit( state, { commitmentId, } ) {
				const { selectedFilterType, } = state;
				const { commitments, } = state.commitmentPages[ selectedFilterType ];

				let targetCommitment;
				if ( commitmentId === NEW_COMMITMENT_ID ) {
					targetCommitment = state.newCommitment;
				} else {
					targetCommitment = commitments.find( commitment => commitment.id === commitmentId );
				}

				if ( !targetCommitment ) {
					// TODO: error message
					return debug( `attempting to edit a commitment that does not exist: ${ commitmentId }` );
				}

				state.commitmentIdBeingEdited = commitmentId;
				state.contentBeingEdited = targetCommitment.content;
			},
			markSaveComplete( state, { id, } ) {
				const { savesInProgress, } = state;
				savesInProgress.delete( id );
				state.savesInProgress = new Set( savesInProgress );
				state.isSavingACommitment = false;
			},
			markSavingCommitment( state, { id, isSaving, } ) {
				const { savesInProgress, } = state;
				if ( isSaving ) {
					savesInProgress.add( id );
				} else {
					savesInProgress.delete( id );
				}
				state.savesInProgress = new Set( savesInProgress );
				state.isSavingACommitment = isSaving;
			},
			optimisticallyUpdateLocalCommitment( state, { commitment, } ) {
				if ( commitment.isNewCommitment ) {
					const newCommitment = {
						...commitment,
					};
					if ( newCommitment.isCompleted ) {
						newCommitment.completed = moment();
						newCommitment.formattedCompletedDate = newCommitment.completed.format( SHORT_DATE_FORMAT );
					} else {
						newCommitment.completed = undefined;
						newCommitment.formattedCompletedDate = "";
					}
					state.newCommitment = newCommitment;
					return;
				}

				const commitmentPages = {
					...state.commitmentPages,
				};

				for ( const filterType of CommitmentFilterType ) {
					const page = commitmentPages[ filterType ];
					const newCommitmentsState = [];
					page.commitments.forEach( pageCommitment => {
						let updatedCommitment = pageCommitment;
						if ( pageCommitment.id === commitment.id ) {
							updatedCommitment = {
								...pageCommitment,
								...commitment,
							};
						}
						newCommitmentsState.push( updatedCommitment );
					} );

					commitmentPages[ filterType ] = {
						...page,
						commitments: newCommitmentsState,
					};
				}

				state.commitmentPages = commitmentPages;
			},
			pausePaging( state ) {
				state.permitPaging = false;
			},
			receiveFilteredCommitments( state, { commitments, filterType, } ) {
				_receiveCommitments( state, { commitments, filterType, } );
			},
			receiveMetadata( state, { metadata, } ) {
				_receiveMetadata( state, metadata );
			},
			receiveUpdatedCommitment( state, { commitment, } ) {
				// remove this commitment from whichever collection to which
				// it had previously belonged
				const commitmentPages = {
					...state.commitmentPages,
				};

				for ( const filterType of CommitmentFilterType ) {
					const page = commitmentPages[ filterType ];
					const filteredCommitments = page.commitments.filter( stateCommitment => stateCommitment.id !== commitment.id );
					commitmentPages[ filterType ] = {
						...page,
						commitments: filteredCommitments,
					};
				}

				// this is a completed commitment
				if ( commitment.completed ) {
					// prepend it to the complete commitments collection
					const completedPage = commitmentPages[ CommitmentFilterType.COMPLETE ];
					const completedCommitments = [ commitment, ...( completedPage.commitments || [] ), ];
					commitmentPages[ CommitmentFilterType.COMPLETE ] = {
						...completedPage,
						commitments: completedCommitments,
					};
				}

				// this is a regular updated commitment
				if ( !commitment.completed ) {
					// for all filters that this commitment matches, update the
					// commitment collections for each
					for ( const filterType of commitment.matchedFilters ) {
						const page = commitmentPages[ filterType ];
						const updatedCommitments = upcat(
							page.commitments,
							[ commitment, ],
							( a, b ) => a.id === b.id
						);
						commitmentPages[ filterType ] = {
							...page,
							commitments: updatedCommitments,
						};
					}

					// remove it from the completed commitments collection (in case its
					// completion status has been changed)
					const completedPage = commitmentPages[ CommitmentFilterType.COMPLETE ];
					const completedCommitments = [ commitment, ...completedPage.commitments, ];
					commitmentPages[ CommitmentFilterType.COMPLETE ] = {
						...completedPage,
						commitments: completedCommitments.filter(
							completedCommitment => completedCommitment.id !== commitment.id
						),
					};
				}

				state.commitmentPages = commitmentPages;
				state.commitmentUpdatesReceived += 1;
			},
			removeDeletedCommitment( state, { commitmentId, } ) {
				const commitmentPages = {
					...state.commitmentPages,
				};

				for ( const filterType of CommitmentFilterType ) {
					const page = commitmentPages[ filterType ];
					const filteredCommitments = page.commitments.filter( stateCommitment => stateCommitment.id !== commitmentId );
					commitmentPages[ filterType ] = {
						...page,
						commitments: filteredCommitments,
					};
				}

				state.commitmentPages = commitmentPages;
			},
			resumePaging( state ) {
				state.permitPaging = true;
			},
			unmarkCommitmentForTextEdit( state ) {
				state.commitmentIdBeingEdited = "";
				state.contentBeingEdited = "";
			},
			updateCommitmentContent( state, { id, content, } ) {
				if ( state.commitmentIdBeingEdited === id ) {
					state.contentBeingEdited = content;
				}
			},
			resetStore( state, { team, outcomeTypes, commitmentMetadata, commitments, filterType, isSetup, } = {} ) {
				Object.assign( state, pristineState( i18n, getUserProfile() ) );

				if ( team ) {
					state.team = team;
				}

				if ( outcomeTypes ) {
					state.outcomeTypes = outcomeTypes;
				}

				if ( commitmentMetadata ) {
					_receiveMetadata( state, commitmentMetadata );
				}

				if ( commitments && filterType ) {
					_receiveCommitments( state, { commitments, filterType, } );
				}

				state.isSetup = !!isSetup;
			},
		},
		actions: {
			async setupStore( { commit, }, { teamId = "", outcomeTypes = [], } = {} ) {
				try {
					commit( "pausePaging" );

					const { personId, } = getUserProfile();

					const {
						team: pristineTeam,
						pageSize: limit,
						selectedFilterType,
						commitmentPages,
					} = pristineState( i18n, getUserProfile() );

					const { offset, } = commitmentPages[ selectedFilterType ];

					const fetchCommitmentsParams = {
						limit,
						offset,
						filterType: selectedFilterType,
						teamId: teamId ? teamId : undefined,
						personId: teamId ? undefined : personId,
						outcomeType: outcomeTypes.length ? outcomeTypes : undefined,
					};

					const promises = [
						teamId ?
							ioTeam.fetchTeam( { teamId, } ) :
							Promise.resolve( { data: pristineTeam, } ),
						teamId ?
							ioOutcome.fetchCommitmentMetadata( { teamId, } ) :
							ioOutcome.fetchCommitmentMetadata(),
						ioOutcome.fetchCommitments( fetchCommitmentsParams ),
					];

					const [
						teamResponse,
						commitmentMetadataResponse,
						commitmentsResponse,
					] = await Promise.all( promises );

					commit( "resetStore", {
						team: teamResponse.data,
						outcomeTypes,
						commitmentMetadata: commitmentMetadataResponse.data,
						commitments: commitmentsResponse.data,
						filterType: selectedFilterType,
						isSetup: true,
					} );
				} catch ( e ) {
					error( "unexpectedError", e );
				} finally {
					commit( "resumePaging" );
				}
			},
			async tryFilterCommitments( { state, commit, }, { filterType, } ) {
				try {
					commit( "pausePaging" );
					commit( "clearNewCommitment" );
					commit( "unmarkCommitmentForTextEdit" );

					const {
						team,
						outcomeTypes,
						pageSize: limit,
					} = state;

					const { personId, } = getUserProfile();

					const fetchCommitmentsParams = {
						limit,
						offset: 0,
						filterType,
						teamId: team.id ? team.id : undefined,
						personId: team.id ? undefined : personId,
						outcomeType: outcomeTypes.length ? outcomeTypes : undefined,
					};

					const commitmentResponse = await ioOutcome.fetchCommitments( fetchCommitmentsParams );

					commit( "receiveFilteredCommitments", {
						commitments: commitmentResponse.data,
						filterType,
					} );
				} catch ( e ) {
					error( "filterCommitmentsError", e );
				} finally {
					commit( "resumePaging" );
				}
			},
			async tryPageCommitments( { state, getters, commit, } ) {
				const {
					team,
					outcomeTypes,
					pageSize: limit,
				} = state;

				const {
					pagingOffset: offset,
					selectedFilterType: filterType,
					permitPaging,
					hasMorePages,
				} = getters;

				if ( !( permitPaging && hasMorePages ) ) {
					return debug( "unable to page", { permitPaging, hasMorePages, } );
				}

				try {
					commit( "pausePaging" );
					commit( "clearNewCommitment" );

					const { personId, } = getUserProfile();

					const fetchCommitmentsParams = {
						limit,
						offset,
						filterType,
						teamId: team.id ? team.id : undefined,
						personId: team.id ? undefined : personId,
						outcomeType: outcomeTypes.length ? outcomeTypes : undefined,
					};

					const commitmentResponse = await ioOutcome.fetchCommitments( fetchCommitmentsParams );

					commit( "receiveFilteredCommitments", {
						commitments: commitmentResponse.data,
						filterType,
					} );
				} catch ( e ) {
					error( "pageCommitmentsError", e );
				} finally {
					commit( "resumePaging" );
				}
			},
			async tryAssignMember( { state, commit, }, { commitment, member, } ) {
				if ( state.savesInProgress.has( commitment.id ) ) {
					return;
				}

				try {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: true, } );

					const isUnAssigning = !member;

					commitment.assignments = [ {
						outcomeId: commitment.id,
						assigned: member,
					}, ];

					if ( commitment.isNewCommitment ) {
						commitment.assignees = [ member, ];
						return commit( "optimisticallyUpdateLocalCommitment", { commitment, } );
					} else if ( !isUnAssigning ) {
						commit( "optimisticallyUpdateLocalCommitment", { commitment, } );
					}

					const { data: updatedCommitment, } = await ioOutcome.updateCommitment( { commitment, } );
					commit( "receiveUpdatedCommitment", { commitment: updatedCommitment, } );

					success( "saveCommitmentSuccess" );
				} catch ( e ) {
					error( "saveCommitmentError", e );
				} finally {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: false, } );
					commit( "markSaveComplete", { id: commitment.id, } );
				}
			},
			async tryChangeDueDate( { state, commit, }, { commitment, dueDate, } ) {
				if ( state.savesInProgress.has( commitment.id ) ) {
					return;
				}

				try {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: true, } );
					commitment.dueDate = dueDate;
					commit( "optimisticallyUpdateLocalCommitment", { commitment, } );

					if ( commitment.id === NEW_COMMITMENT_ID ) {
						return;
					}

					const { data: updatedCommitment, } = await ioOutcome.updateCommitment( { commitment, } );
					commit( "receiveUpdatedCommitment", { commitment: updatedCommitment, } );

					success( "saveCommitmentSuccess" );
				} catch ( e ) {
					error( "saveCommitmentError", e );
				} finally {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: false, } );
					commit( "markSaveComplete", { id: commitment.id, } );
				}
			},
			async trySaveCommitment( { state, commit, }, { commitment, } ) {
				if ( state.savesInProgress.has( commitment.id ) ) {
					return debug( "pending save in progress already" );
				}

				const {
					isDirty,
					isContentValid,
				} = commitment;

				if ( !isContentValid ) {
					return debug( "validation errors" );
				}

				if ( !isDirty ) {
					debug( "no changes to save" );
					return commit( "unmarkCommitmentForTextEdit" );
				}

				try {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: true, } );

					const preSaveCommitment = {
						...commitment,
						content: commitment.contentBeingEdited,
					};

					commit( "optimisticallyUpdateLocalCommitment", { commitment: preSaveCommitment, } );

					if ( commitment.isNewCommitment ) {
						const { data: createdCommitment, } = await ioOutcome.createCommitment( { commitment: preSaveCommitment, } );
						debug( createdCommitment );
						return commit( "receiveUpdatedCommitment", { commitment: createdCommitment, } );
					}

					const { data: updatedCommitment, } = await ioOutcome.updateCommitment( { commitment: preSaveCommitment, } );
					commit( "receiveUpdatedCommitment", { commitment: updatedCommitment, } );

					success( "saveCommitmentSuccess" );
				} catch ( e ) {
					error( "saveCommitmentError", e );
				} finally {
					commit( "clearNewCommitment" );
					commit( "markSavingCommitment", { id: commitment.id, isSaving: false, } );
					commit( "markSaveComplete", { id: commitment.id, } );
				}
			},
			async tryDeleteCommitment( { state, commit, }, { commitment, } ) {
				if ( state.savesInProgress.has( commitment.id ) ) {
					return;
				}

				try {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: true, } );

					if ( commitment.isNewCommitment ) {
						return commit( "clearNewCommitment" );
					}

					await ioOutcome.deleteOutcome( { outcomeId: commitment.id, } );
					commit( "removeDeletedCommitment", { commitmentId: commitment.id, } );

					success( "deleteCommitmentSuccess" );
				} catch ( e ) {
					error( "deleteCommitmentError", e );
				} finally {
					commit( "markSavingCommitment", { id: commitment.id, isSaving: false, } );
					commit( "markSaveComplete", { id: commitment.id, } );
				}
			},
		},
	};
}
